'use client';

import { useEffect, useState } from 'react';
import { Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ResumeDrawer } from '@/components/admin/resume-drawer';
import { candidateApi } from '@/lib/api';

/**
 * Candidate-facing "View my resume" slide-over for the returning-candidate
 * apply step (GOAL 3). Shows ONLY the resume document preview — no parsed
 * profile, no skills — fed by the AUTH-GATED /api/candidates/me/resumes/:id/file
 * route (never the unauthenticated /api/files/resumes/* path).
 *
 * The on-file resume's mime_type / file_name are resolved from the candidate's
 * own resume list so a PDF previews inline and a Word doc gets the correct
 * download fallback. The fetch is best-effort: if it fails or the id is missing,
 * the drawer still opens with working Open / Download actions.
 */
export function ReturningResumeDrawer({ resumeId }: { resumeId: string }) {
  const [meta, setMeta] = useState<{ mimeType?: string; fileName?: string }>({});

  useEffect(() => {
    let cancelled = false;
    candidateApi
      .getResumes()
      .then((res) => {
        if (cancelled || !res.success || !res.data) return;
        const row = res.data.find((r) => r.id === resumeId);
        if (row) setMeta({ mimeType: row.mime_type, fileName: row.file_name });
      })
      .catch(() => {
        /* best-effort — Open / Download still work without the type hint */
      });
    return () => {
      cancelled = true;
    };
  }, [resumeId]);

  return (
    <ResumeDrawer
      resumeUrl={candidateApi.getResumeFileUrl(resumeId)}
      resumeId={resumeId}
      mimeType={meta.mimeType}
      fileName={meta.fileName}
    >
      <Button type="button" variant="outline" size="sm">
        <Eye className="w-4 h-4" /> View my resume
      </Button>
    </ResumeDrawer>
  );
}
